// Text-booking shortcut — sibling of VoiceBookingButton. The customer types a
// free-form description ("Ich brauche morgen 14 Uhr einen Umzug von Eimsbüttel
// nach Altona") and Claude extracts service / date / time / address / details
// to prefill the form.
import { useState } from "react";
import { Type, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

export default function TextBookingButton({ onFields, lang }) {
  const { t, lang: ctxLang } = useLang();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const labels = t.text_booking || {};

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErrorMsg("");
    try {
      const r = await api.post("/text/booking-intent", {
        text: text.trim(),
        lang: lang || ctxLang || "de",
      }, { timeout: 60000 });
      if (r.data?.ok && r.data?.fields && onFields) {
        onFields(r.data.fields);
        setOpen(false);
        setText("");
      } else {
        setErrorMsg(labels.error || "Keine Felder erkannt.");
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || labels.error || "Fehler bei der Verarbeitung.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-neutral-300 bg-gradient-to-br from-neutral-50 to-white p-5 mb-6" data-testid="text-booking-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-neutral-900 flex items-center justify-center text-white flex-shrink-0">
          <Type size={18} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-neutral-900 mb-1">
            {labels.title || "Text-Buchung"}
          </div>
          <p className="text-sm text-neutral-700 leading-snug mb-3">
            {labels.subtitle || "Beschreiben Sie Ihren Auftrag in Worten — KI füllt das Formular automatisch."}
          </p>
          {!open && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition rounded-sm"
              data-testid="text-booking-open"
            >
              <Sparkles size={14} /> {labels.start || "Text eingeben"}
            </button>
          )}
          {open && (
            <div data-testid="text-booking-form">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder={labels.placeholder || "z.B. 'Ich brauche morgen 14 Uhr einen Umzug von Eimsbüttel nach Altona, 2 Zimmer'"}
                className="w-full border border-neutral-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-neutral-900 mb-2"
                disabled={busy}
                data-testid="text-booking-input"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !text.trim()}
                  className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black text-white font-bold uppercase tracking-wide text-xs px-4 py-2 transition rounded-sm disabled:opacity-50"
                  data-testid="text-booking-submit"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {busy ? (labels.processing || "Verarbeite…") : (labels.submit || "Auto-füllen")}
                </button>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setText(""); setErrorMsg(""); }}
                  className="text-xs text-neutral-500 hover:text-neutral-900 font-bold uppercase tracking-wider"
                  data-testid="text-booking-cancel"
                >
                  {labels.cancel || "Abbrechen"}
                </button>
              </div>
              {errorMsg && (
                <div className="text-xs text-red-600 mt-2" data-testid="text-booking-error">{errorMsg}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
